import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Eye, Loader2, MailCheck, MessageSquareReply, MousePointerClick, Send } from 'lucide-react';
import { useState } from 'react';

import { apiClient, type PageEnvelope } from '@/lib/api-client';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Loader } from '@/components/ui/loader';
import { Modal } from '@/components/ui/modal';
import { StatusPill } from '@/components/ui/status-pill';
import { Topbar } from '@/components/ui/topbar';
import { sanitizeEmailHtml } from '@/lib/sanitize-email-html';

interface MessageRow {
  id: string;
  campaign_id: string | null;
  campaign_name: string | null;
  lead_id: string | null;
  lead_email: string | null;
  lead_name: string | null;
  lead_company: string | null;
  mailbox_id: string | null;
  mailbox_email: string | null;
  step_number: number | null;
  subject: string | null;
  body_preview: string | null;
  body_html?: string | null;
  status: string;
  scheduled_at: string | null;
  sent_at: string | null;
  opened_at: string | null;
  opens_count: number;
  clicked_at: string | null;
  clicks_count: number;
  replied_at: string | null;
  bounced_at: string | null;
  error: string | null;
}

export function SentPage(): JSX.Element {
  const [filter, setFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [previewId, setPreviewId] = useState<string | null>(null);

  const list = useInfiniteQuery({
    queryKey: ['messages', 'sent', filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      apiClient.get<PageEnvelope<MessageRow>>('/api/v1/messages', {
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
        ...(filter ? { status: filter } : {}),
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const messages: MessageRow[] = list.data
    ? list.data.pages.flatMap((p) => p.items)
    : [];

  const filtered = messages.filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (m.subject ?? '').toLowerCase().includes(q) ||
      (m.lead_email ?? '').toLowerCase().includes(q) ||
      (m.lead_name ?? '').toLowerCase().includes(q) ||
      (m.campaign_name ?? '').toLowerCase().includes(q) ||
      (m.body_preview ?? '').toLowerCase().includes(q)
    );
  });

  const counts = messages.reduce<Record<string, number>>((acc, m) => {
    acc[m.status] = (acc[m.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <Topbar
        title="Sent messages"
        description={`${filtered.length} of ${messages.length}`}
      />
      <div className="flex-1 overflow-auto p-6 space-y-4">
        {/* Status filter chips */}
        <div className="flex flex-wrap gap-2 items-center">
          {[
            { key: '', label: 'All', n: messages.length },
            { key: 'sent', label: 'Sent', n: counts.sent ?? 0 },
            { key: 'queued', label: 'Queued', n: counts.queued ?? 0 },
            { key: 'sending', label: 'Sending', n: counts.sending ?? 0 },
            { key: 'failed', label: 'Failed', n: counts.failed ?? 0 },
          ].map((c) => (
            <button
              key={c.key || 'all'}
              type="button"
              onClick={() => setFilter(c.key)}
              className={
                'rounded-md border px-3 py-1.5 text-xs transition-colors ' +
                (filter === c.key
                  ? 'border-mb-accent bg-mb-accent-soft text-mb-accent-press font-medium'
                  : 'border-mb-border bg-mb-surface text-mb-text-muted hover:border-mb-text-muted')
              }
            >
              {c.label} · <b>{c.n}</b>
            </button>
          ))}
          <input
            type="search"
            placeholder="Search subject / recipient / campaign / body"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-input ml-auto min-w-64 flex-1"
          />
        </div>

        {list.isLoading ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : list.isError ? (
          <ErrorState onRetry={() => void list.refetch()} />
        ) : messages.length === 0 ? (
          <EmptyState
            icon={Send}
            title="No messages yet"
            description="Once you launch a campaign, every email it sends shows up here."
          />
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-sm text-mb-text-muted">
            No messages match your filter/search.
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((m) => (
              <MessageCard key={m.id} m={m} onClick={() => setPreviewId(m.id)} />
            ))}
            {list.hasNextPage ? (
              <div className="flex justify-center pt-3">
                <button
                  type="button"
                  className="mb-btn-secondary"
                  disabled={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  {list.isFetchingNextPage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  Load 50 more
                </button>
              </div>
            ) : messages.length > 0 ? (
              <div className="text-center text-xs text-mb-text-subtle pt-3 pb-2">
                — end of list ({messages.length} loaded) —
              </div>
            ) : null}
          </div>
        )}
      </div>

      <MessagePreviewModal
        messageId={previewId}
        onClose={() => setPreviewId(null)}
      />
    </>
  );
}

/**
 * Fetches the full rendered HTML body from /messages/{id} and shows it
 * inside a Gmail-style envelope: From / To / Subject header + the actual
 * body underneath. Sanitized before render so malicious HTML never
 * executes in the operator's browser.
 */
function MessagePreviewModal({
  messageId,
  onClose,
}: {
  messageId: string | null;
  onClose: () => void;
}): JSX.Element {
  const detail = useQuery({
    queryKey: ['message-detail', messageId],
    queryFn: () => apiClient.get<MessageRow>(`/api/v1/messages/${messageId}`),
    enabled: Boolean(messageId),
    staleTime: 30_000,
  });

  const open = Boolean(messageId);
  const m = detail.data;

  return (
    <Modal open={open} onClose={onClose} title={m?.subject ?? 'Message preview'} size="lg">
      {detail.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      ) : detail.isError || !m ? (
        <ErrorState onRetry={() => void detail.refetch()} />
      ) : (
        <div className="space-y-3">
          {/* Header meta strip */}
          <div className="rounded-md border border-mb-border bg-mb-surface px-3 py-2 text-xs flex items-center gap-2 flex-wrap">
            <StatusPill status={m.status} />
            {m.campaign_name ? (
              <span className="text-mb-text-muted">
                {m.campaign_name}
                {m.step_number ? ` · step ${m.step_number}` : ''}
              </span>
            ) : null}
            <span className="ml-auto text-mb-text-subtle">
              {m.sent_at
                ? `sent ${new Date(m.sent_at).toLocaleString()}`
                : m.scheduled_at
                  ? `scheduled ${new Date(m.scheduled_at).toLocaleString()}`
                  : ''}
            </span>
          </div>

          {/* Envelope */}
          <div className="rounded-md border border-mb-border bg-white overflow-hidden">
            <div className="border-b border-mb-border bg-mb-surface px-4 py-2 text-xs">
              <div>
                <span className="text-mb-text-subtle">From: </span>
                <span className="font-mono text-mb-text">
                  {m.mailbox_email ?? 'unknown'}
                </span>
              </div>
              <div className="mt-0.5">
                <span className="text-mb-text-subtle">To: </span>
                <span className="font-mono text-mb-text">
                  {m.lead_email ?? 'unknown'}
                </span>
                {m.lead_name ? (
                  <span className="text-mb-text-muted"> · {m.lead_name}</span>
                ) : null}
                {m.lead_company ? (
                  <span className="text-mb-text-muted"> · {m.lead_company}</span>
                ) : null}
              </div>
              <div className="mt-1 text-sm font-medium text-mb-text">
                {m.subject || (
                  <span className="italic text-mb-text-subtle">(no subject)</span>
                )}
              </div>
            </div>
            <div
              className="prose prose-sm max-w-none px-4 py-4 text-sm text-mb-text"
              dangerouslySetInnerHTML={{
                __html: m.body_html
                  ? sanitizeEmailHtml(m.body_html)
                  : m.body_preview
                    ? `<pre>${escapeHtml(m.body_preview)}</pre>`
                    : '<i style="color:#999">(empty body)</i>',
              }}
            />
          </div>

          {/* Engagement chips */}
          <div className="flex items-center gap-2 text-xs text-mb-text-muted flex-wrap">
            {m.opened_at ? (
              <span className="inline-flex items-center gap-1 rounded bg-mb-accent-soft px-1.5 py-0.5 text-mb-accent-press">
                <Eye className="h-3 w-3" /> {m.opens_count} open{m.opens_count === 1 ? '' : 's'}
              </span>
            ) : null}
            {m.clicked_at ? (
              <span className="inline-flex items-center gap-1 rounded bg-mb-accent-soft px-1.5 py-0.5 text-mb-accent-press">
                <MousePointerClick className="h-3 w-3" /> {m.clicks_count} click{m.clicks_count === 1 ? '' : 's'}
              </span>
            ) : null}
            {m.replied_at ? (
              <span className="inline-flex items-center gap-1 rounded bg-mb-success-soft px-1.5 py-0.5 text-mb-success">
                <MessageSquareReply className="h-3 w-3" /> replied
              </span>
            ) : null}
            {m.bounced_at ? (
              <span className="inline-flex items-center gap-1 rounded bg-mb-danger-soft px-1.5 py-0.5 text-mb-danger">
                <AlertTriangle className="h-3 w-3" /> bounced
              </span>
            ) : null}
            {m.error ? (
              <span className="text-mb-danger">Error: {m.error}</span>
            ) : null}
          </div>
        </div>
      )}
    </Modal>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function MessageCard({ m, onClick }: { m: MessageRow; onClick: () => void }): JSX.Element {
  const when = m.sent_at ?? m.scheduled_at;
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-card w-full text-left p-4 transition-colors hover:border-mb-accent focus:outline-none focus:ring-2 focus:ring-mb-accent"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={m.status} />
            {m.campaign_name ? (
              <span className="text-xs text-mb-text-muted">
                {m.campaign_name}
                {m.step_number ? ` · step ${m.step_number}` : ''}
              </span>
            ) : null}
            {m.mailbox_email ? (
              <span className="text-[10px] text-mb-text-subtle font-mono">
                from {m.mailbox_email}
              </span>
            ) : null}
          </div>

          <div className="mt-1.5 font-medium text-mb-text truncate">
            {m.subject ?? '(no subject)'}
          </div>

          <div className="text-xs text-mb-text-muted mt-0.5 truncate">
            to <span className="font-mono">{m.lead_email ?? 'unknown'}</span>
            {m.lead_name ? <> · {m.lead_name}</> : null}
            {m.lead_company ? <> · {m.lead_company}</> : null}
          </div>

          {m.body_preview ? (
            <div className="mt-2 text-xs text-mb-text-muted line-clamp-2 border-l-2 border-mb-border pl-2 italic">
              {m.body_preview}
            </div>
          ) : null}

          {m.error ? (
            <div className="mt-2 rounded border border-mb-danger/30 bg-mb-danger-soft px-2 py-1 text-xs text-mb-danger">
              {m.error}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col items-end shrink-0 text-right">
          <div className="text-[10px] text-mb-text-subtle">
            {when ? new Date(when).toLocaleString() : '—'}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1 justify-end">
            {m.status === 'sent' && (
              <span className="inline-flex items-center gap-1 text-[10px] text-mb-success">
                <MailCheck className="h-3 w-3" /> sent
              </span>
            )}
            {m.opened_at && (
              <span className="inline-flex items-center gap-1 rounded bg-mb-accent-soft px-1.5 py-0.5 text-[10px] text-mb-accent-press">
                <Eye className="h-3 w-3" /> {m.opens_count} open{m.opens_count === 1 ? '' : 's'}
              </span>
            )}
            {m.clicked_at && (
              <span className="inline-flex items-center gap-1 rounded bg-mb-accent-soft px-1.5 py-0.5 text-[10px] text-mb-accent-press">
                <MousePointerClick className="h-3 w-3" /> {m.clicks_count} click{m.clicks_count === 1 ? '' : 's'}
              </span>
            )}
            {m.replied_at && (
              <span className="inline-flex items-center gap-1 rounded bg-mb-success-soft px-1.5 py-0.5 text-[10px] text-mb-success">
                <MessageSquareReply className="h-3 w-3" /> replied
              </span>
            )}
            {m.bounced_at && (
              <span className="inline-flex items-center gap-1 rounded bg-mb-danger-soft px-1.5 py-0.5 text-[10px] text-mb-danger">
                <AlertTriangle className="h-3 w-3" /> bounced
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
